import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Container, Form, Modal, Row, Card, Spinner } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import { Prompt } from 'react-router';
import {
  LiveEventShopItemConfig,
} from '../../../../../../__gqltypes__';
import { applyChangeIfDateValid } from '../../../../../../utils/datepicker';
import ContentPackListEditor from '../../../../curation/screens/ContentPackList/ContentPackListEditor';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import AssetDisplay from '../../Items/components/AssetDisplay';
import DragUpload from '../../Items/components/DragUpload';
import { MilestoneValue } from '../../Tournament/components/MileStones';
import { RankGroupValue } from '../../Tournament/components/RankGroups';
import {
  EXPORT_LIVE_EVENT,
  GET_LIVE_EVENT,
  REMOVE_LIVE_EVENT,
  UPDATE_LIVE_EVENT,
  UPDATE_LIVE_EVENT_CONTENT_PACKS,
  UPDATE_LIVE_EVENT_SHOP,
} from '../graphql';
import { LiveEventShop } from './LiveEventShop';
import ExportLiveEventModal from './ExportLiveEventModal';
import {
  convertLiveEventShopItemToInputs,
  detectLiveEventDataChanges,
  LiveEventEditSnapshot,
  liveEventMatcherSignature,
} from '../utils';
import {
  eventMatcherDefaultConfig,
  EventMatchers,
  EventMatchersConfig,
  userIdsArrayToString,
  userIdsStringToArray,
} from '../../../general/EventMatchers';
import { DetectedLiveChanges, hasLiveChanges, highestChangeRisk } from '../../Marathon/utils';

/**
 * A component showing the data of a liveEvent
 */
type Props = {
  liveEventId: string;
  onUpsertLiveEvent: (data: any) => void;
  onRemoveLiveEvent: (data: any) => void;
};

const LiveEventData = ({ liveEventId, onUpsertLiveEvent, onRemoveLiveEvent }: Props) => {
  /**
   * State
   */

  // Currently displayed liveEvent
  /**
   * @type {[LiveEvent, Function]} Currently displayed LiveEvent
   */
  const [name, setName] = useState('');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const [isFilterOnlyAdmin, setIsFilterOnlyAdmin] = useState(false);
  const [filterConfig, setFilterConfig] = useState<EventMatchersConfig>(eventMatcherDefaultConfig);
  const [readonly, setReadonly] = useState(true);
  const [shop, setShop] = useState<LiveEventShopItemConfig[]>([]);
  const [contentPackIds, setContentPackIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // pop-up state management ----------
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportJson, setExportJson] = useState<string | null>(null);

  /**
   * FETCHING
   */
  const {
    data,
    loading: loadingLiveEvent,
    refetch,
  } = useQuery(GET_LIVE_EVENT, {
    fetchPolicy: 'no-cache',
    variables: { liveEventId },
  });

  // No onError: Apollo would swallow the failure and saveAll could not stop or tell the operator
  const [updateLiveEvent] = useMutation(UPDATE_LIVE_EVENT);

  const [updateLiveEventContentPacks] = useMutation(UPDATE_LIVE_EVENT_CONTENT_PACKS);

  const [updateLiveEventShop] = useMutation(UPDATE_LIVE_EVENT_SHOP);

  const [exportLiveEvent, { loading: exportLoading }] = useMutation(EXPORT_LIVE_EVENT, {
    onCompleted: ({ exportLiveEvent: result }) => {
      setExportJson(result?.json ?? '');
    },
    onError: (error) => {
      console.log(error);
      setExportJson('');
    },
  });

  const [removeLiveEvent, { loading: removeLoading }] = useMutation(REMOVE_LIVE_EVENT, {
    onCompleted: ({ removeLiveEvent: _removeLiveEvent }) => {
      if (!_removeLiveEvent.isRemoved) {
        alert("ERROR: Couldn't remove the current liveEvent");
        return;
      }
      onRemoveLiveEvent(liveEventId);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // Only the first load replaces the page; saving keeps it mounted so shop / content pack edits survive
  const loading = (loadingLiveEvent && !data) || removeLoading;

  /**
   * STATE AUTO UPDATE FUNCTIONS
   */
  // set current liveEvent to the one fetched
  useEffect(() => {
    if (data && data.liveEvent) {
      const fetchedLiveEvent = data.liveEvent;
      setName(fetchedLiveEvent.name);
      setStartDate(new Date(fetchedLiveEvent.startDate));
      setEndDate(new Date(fetchedLiveEvent.endDate));
      setIsDraft(fetchedLiveEvent.isDraft);
      setIsFilterOnlyAdmin(fetchedLiveEvent.filterOnlyAdmin);
      setFilterConfig(matchersFromLiveEvent(fetchedLiveEvent));
    } else if (!data || !data.liveEvent) {
      setName('');
      setStartDate(null);
      setEndDate(null);
      setIsDraft(false);
      setIsFilterOnlyAdmin(false);
      setFilterConfig(eventMatcherDefaultConfig);
    }
  }, [data]);

  useEffect(() => {
    setReadonly(loading || saving);
  }, [loading, saving, data]);

  // Live = published and inside its window, as last saved (players are mid-event right now)
  const isLive = useMemo(() => {
    const loaded = data?.liveEvent;
    if (!loaded || loaded.isDraft) {
      return false;
    }
    const now = Date.now();
    return new Date(loaded.startDate).getTime() <= now && now < new Date(loaded.endDate).getTime();
  }, [data]);

  const loadedSnapshot = useMemo<LiveEventEditSnapshot | null>(() => {
    const loaded = data?.liveEvent;
    if (!loaded) {
      return null;
    }
    return {
      name: loaded.name,
      startDate: new Date(loaded.startDate),
      endDate: new Date(loaded.endDate),
      isDraft: loaded.isDraft,
      filterOnlyAdmin: loaded.filterOnlyAdmin,
      matchers: matchersFromLiveEvent(loaded),
    };
  }, [data]);

  const [pendingConfirm, setPendingConfirm] = useState<DetectedLiveChanges | null>(null);
  const pendingRisk = pendingConfirm ? highestChangeRisk(pendingConfirm) : null;

  // Sections with edits not saved yet; the floating Save writes exactly these
  const isSettingsDirty = useMemo(() => {
    if (!loadedSnapshot) {
      return false;
    }
    return (
      loadedSnapshot.name !== name ||
      epochOf(loadedSnapshot.startDate) !== epochOf(startDate) ||
      epochOf(loadedSnapshot.endDate) !== epochOf(endDate) ||
      loadedSnapshot.isDraft !== isDraft ||
      loadedSnapshot.filterOnlyAdmin !== isFilterOnlyAdmin ||
      liveEventMatcherSignature(loadedSnapshot.matchers) !== liveEventMatcherSignature(filterConfig)
    );
  }, [loadedSnapshot, name, startDate, endDate, isDraft, isFilterOnlyAdmin, filterConfig]);

  const isShopDirty = useMemo(() => {
    const loadedShop = data?.liveEvent?.shop;
    if (!loadedShop) {
      return false;
    }
    return (
      JSON.stringify(convertLiveEventShopItemToInputs(loadedShop)) !==
      JSON.stringify(convertLiveEventShopItemToInputs(shop))
    );
  }, [data, shop]);

  const isContentPacksDirty = useMemo(() => {
    const loadedPacks = data?.liveEvent?.contentPacks;
    if (!loadedPacks) {
      return false;
    }
    return !_.isEqual(
      loadedPacks.map((pack: { id: string }) => pack.id),
      contentPackIds
    );
  }, [data, contentPackIds]);

  const dirtySections = [
    isSettingsDirty && 'Event settings',
    isShopDirty && 'Shop',
    isContentPacksDirty && 'Content packs',
  ].filter(Boolean) as string[];
  const isDirty = dirtySections.length > 0;

  // Closing / reloading the tab; switching event inside the admin is caught by <Prompt>
  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  /**
   * CHANGES HANDLERS FUNCTIONS
   */
  // Saves every dirty section, then refetches once. The refetch resets the form to the server state,
  // so it only runs when everything went through; on a failure the unsaved edits stay on screen.
  const saveAll = async () => {
    setSaving(true);
    const failed: string[] = [];
    if (isSettingsDirty) {
      try {
        const result = await updateLiveEvent({
          variables: {
            input: {
              liveEventId,
              name,
              startDate,
              endDate,
              isDraft,
              filterOnlyAdmin: isFilterOnlyAdmin,
              filterUserIds: userIdsStringToArray(filterConfig.filterUserIds ?? '') ?? [],
              filterSubscriptionTier: filterConfig.filterSubscriptionTier ?? null,
              filterUserMinCreationDate: filterConfig.filterUserMinCreationDate ?? null,
              filterUserMaxCreationDate: filterConfig.filterUserMaxCreationDate ?? null,
              filterAppVersion: filterConfig.filterAppVersion || null,
              filterAppVersionOperator: filterConfig.filterAppVersionOperator ?? null,
              filterUserLevel: filterConfig.filterUserLevel ?? null,
            },
          },
        });
        onUpsertLiveEvent(result.data.updateLiveEvent.liveEvent);
      } catch (error) {
        console.log(error);
        failed.push(`Event settings: ${(error as Error).message}`);
      }
    }
    if (isShopDirty) {
      try {
        await updateLiveEventShop({
          variables: { input: { liveEventId, shopItemIds: convertLiveEventShopItemToInputs(shop) } },
        });
      } catch (error) {
        console.log(error);
        failed.push(`Shop: ${(error as Error).message}`);
      }
    }
    if (isContentPacksDirty) {
      try {
        await updateLiveEventContentPacks({ variables: { input: { liveEventId, contentPackIds } } });
      } catch (error) {
        console.log(error);
        failed.push(`Content packs: ${(error as Error).message}`);
      }
    }
    if (failed.length > 0) {
      setSaving(false);
      alert(`Some changes were not saved. Your edits are still on the page, fix them and save again.\n\n${failed.join('\n')}`);
      return;
    }
    await refetch();
    setSaving(false);
  };

  const handleSave = () => {
    // live events: confirm first, naming what changed vs the loaded event
    if (isLive && loadedSnapshot && isSettingsDirty) {
      setPendingConfirm(
        detectLiveEventDataChanges(loadedSnapshot, {
          name,
          startDate,
          endDate,
          isDraft,
          filterOnlyAdmin: isFilterOnlyAdmin,
          matchers: filterConfig,
        })
      );
      return;
    }
    saveAll();
  };

  const confirmSave = () => {
    setPendingConfirm(null);
    saveAll();
  };

  const handleRemoveLiveEvent = () => {
    removeLiveEvent({
      variables: {
        input: {
          liveEventId,
        },
      },
    });
  };

  const handleExport = () => {
    setExportJson(null);
    setShowExportModal(true);
    exportLiveEvent({ variables: { input: { liveEventId } } });
  };

  const handleChangeName = useCallback((event) => {
    setName(event.target.value);
  }, []);

  const handleShopUpdate = useCallback((updatedShop: LiveEventShopItemConfig[]) => {
    setShop(updatedShop);
    return shop;
  }, [shop]);

  /**
   * RENDER
   */
  return loading ? (
    <LoadingLogo show />
  ) : (
    <Container className="pb-4">
      {isLive && (
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle mr-2" />
          This event is live. Changing dates, draft, admin only or matchers can hide it from players.
        </Alert>
      )}
      <Row className="mb-3">
        <Col md={4}>Start date UTC:</Col>
        <Col md={4}>
          <DateTimePicker
            // For the moment, only 00:00 formats are expects
            dateFormat="YYYY-MM-DD"
            utc={true}
            timeFormat={true}
            timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
            onChange={applyChangeIfDateValid(setStartDate)}
            value={startDate ?? undefined}
          />
        </Col>
        <Col className="d-flex justify-content-end">
          <Button className="mr-2" onClick={handleExport} variant="info" disabled={readonly}>
            <i className="fas fa-file-export mr-2" />
            Export
          </Button>
          <Button
            className="mr-2"
            onClick={() => {
              setShowRemoveModal(!showRemoveModal);
            }}
            variant="danger"
            disabled={readonly}
          >
            <i className="fas fa-trash mr-2" />
            Delete
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>End date UTC:</Col>
        <Col md={4}>
          <DateTimePicker
            // For the moment, only 00:00 formats are expects
            dateFormat="YYYY-MM-DD"
            utc={true}
            timeFormat={true}
            timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
            onChange={applyChangeIfDateValid(setEndDate)}
            value={endDate ?? undefined}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}> Name:</Col>
        <Col md={4}>
          <Form.Control disabled={readonly} value={name} onChange={handleChangeName} type="text" />
        </Col>
      </Row>

      <Row>
        <Col md={4}>Is Draft</Col>
        <Col md={4}>
          <Form.Check
            type="checkbox"
            checked={isDraft}
            onChange={({ target }) => {
              setIsDraft(target.checked);
            }}
          />
        </Col>
      </Row>
      <hr />
      <Row>
        <Col md={4}>Filter Admin Only</Col>
        <Col md={4}>
          <Form.Check
            type="checkbox"
            checked={isFilterOnlyAdmin}
            onChange={({ target }) => {
              setIsFilterOnlyAdmin(target.checked);
            }}
          />
        </Col>
      </Row>
      <hr />
      <EventMatchers
        config={filterConfig}
        onUpdate={setFilterConfig}
        highlight
        showAppVersionOperator
        collapsible={false}
      />
      <hr />
      <Row>
        <Card style={{width: '100%'}}>
          <Card.Header className="text-left">
            <Card.Title>Shop</Card.Title>
          </Card.Header>
          <Card.Body>
            {data && data.liveEvent && (
              // @ts-ignore
              <LiveEventShop
                shop={data.liveEvent.shop}
                onUpdateShop={handleShopUpdate}/>
            )}
          </Card.Body>
        </Card>
      </Row>
      <hr />
      {data && data.liveEvent && (
        // @ts-ignore
        <ContentPackListEditor
          contentPacks={data.liveEvent.contentPacks}
          id={liveEventId}
          // saved by the floating Save bar, so the editor's own button is hidden
          updatePackIds={async () => undefined}
          onPacksChange={setContentPackIds}
          hideSaveButton
          enableImport
        />
      )}
      <hr />

      <Prompt when={isDirty} message="You have unsaved changes on this live event. Leave without saving?" />
      <div className="liveevent-savebar d-flex justify-content-between align-items-center px-3 py-2">
        <span className={isDirty ? 'text-warning font-weight-bold' : 'text-muted'}>
          {isDirty ? (
            <>
              <i className="fas fa-exclamation-circle mr-2" />
              Unsaved changes: {dirtySections.join(', ')}
            </>
          ) : (
            <>
              <i className="fas fa-check mr-2" />
              All changes saved
            </>
          )}
        </span>
        <Button onClick={handleSave} variant="success" disabled={!isDirty || readonly}>
          {saving ? (
            <Spinner as="span" animation="border" size="sm" className="mr-2" />
          ) : (
            <i className="fas fa-save mr-2" />
          )}
          Save
        </Button>
      </div>

      <ExportLiveEventModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        loading={exportLoading}
        json={exportJson}
      />

      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Stop the current</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>This cannot be undone. Are you sure to want to remove the current LiveEvent?</p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              handleRemoveLiveEvent();
              setShowRemoveModal(false);
            }}
          >
            Remove Live Event
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pendingConfirm !== null} onHide={() => setPendingConfirm(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {pendingRisk === 'HIGH' ? 'Risky change to a LIVE event' : 'Confirm change to a live event'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingRisk === 'HIGH' ? (
            <Alert variant="danger">
              This live event is <strong>running with players mid-event</strong>. The changes below can{' '}
              <strong>hide the event from players who already earned currency in it</strong>. Their currency and
              purchases stay saved, but they cannot use them while the event is hidden.
            </Alert>
          ) : (
            <Alert variant="info">This live event is running. Please confirm the change before saving.</Alert>
          )}
          {pendingConfirm && hasLiveChanges(pendingConfirm) ? (
            <>
              {pendingConfirm.high.length > 0 && (
                <>
                  <div className="font-weight-bold text-danger">High risk — players can lose the event</div>
                  <ul>
                    {pendingConfirm.high.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {pendingConfirm.low.length > 0 && (
                <>
                  <div className="font-weight-bold text-muted">Low risk — general info</div>
                  <ul>
                    {pendingConfirm.low.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <p className="text-muted mb-0">No changes detected — you are re-saving a live event.</p>
          )}
          <p className="text-muted small mt-3 mb-0">
            Shop and content pack changes are saved together with this, but are not part of this check.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingConfirm(null)}>
            Cancel
          </Button>
          <Button variant={pendingRisk === 'HIGH' ? 'danger' : 'primary'} onClick={confirmSave}>
            {pendingRisk === 'HIGH' ? 'Save anyway' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// react-datetime's onChange hands back a moment.Moment despite the Date typing; new Date() accepts both
const epochOf = (date: Date | null): number | null => (date ? new Date(date).getTime() : null);

// The backend reports legacy filterUserIds / filterVersion through these fields until the event is re-saved
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function matchersFromLiveEvent(liveEvent: any): EventMatchersConfig {
  return {
    filterUserIds: userIdsArrayToString(liveEvent.filterUserIds ?? []),
    filterSubscriptionTier: liveEvent.filterSubscriptionTier ?? undefined,
    filterUserMinCreationDate: liveEvent.filterUserMinCreationDate
      ? new Date(liveEvent.filterUserMinCreationDate)
      : undefined,
    filterUserMaxCreationDate: liveEvent.filterUserMaxCreationDate
      ? new Date(liveEvent.filterUserMaxCreationDate)
      : undefined,
    filterAppVersion: liveEvent.filterAppVersion ?? undefined,
    // absent means '>='; keep it undefined so an untouched save stays operator-less
    filterAppVersionOperator: liveEvent.filterAppVersionOperator ?? undefined,
    filterUserLevel: liveEvent.filterUserLevel ?? undefined,
  };
}
export default LiveEventData;
